import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { StudentInfo } from "../student/studentSlice";
import { InviteResponse } from "../user/createInviteSlice";

export interface StudentInvite {
    student: StudentInfo;
    invite: InviteResponse;
}

export interface ClassroomStudentInvitesState {
    state: LoadStateInfo;
    request?: RetrieveClassroomStudentInvitesRequest;
    list?: StudentInvite[];
}

const initialState: ClassroomStudentInvitesState = {
    state: notStarted(),
};

export interface RetrieveClassroomStudentInvitesRequest {
    classroomId: string;
}

export const retrieveClassroomStudentInvites = createAsyncThunk(
    "retrieveClassroomStudentInvites",
    async (params: RetrieveClassroomStudentInvitesRequest, thunkAPI) => {
        console.debug(`retrieve student invites for classroom ${params.classroomId}`);
        const response = await fetch(`${getBackendUrl()}/api/classroom/${params.classroomId}/student-invites`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveClassroomStudentInvitesRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.classroomStudentInvites;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveClassroomStudentInvites skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const classroomStudentInvitesSlice = createSlice({
    name: "classroomStudentInvites",
    initialState,
    reducers: {
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveClassroomStudentInvites.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveClassroomStudentInvites.fulfilled, (state, action) => {
                state.state = loaded();
                state.list = action.payload;
            })
            .addCase(retrieveClassroomStudentInvites.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const selectClassroomStudentInvites = (state: RootState) => state.classroomStudentInvites;

export default classroomStudentInvitesSlice.reducer;
